



























































































import {Component, Prop, Vue, Watch} from "vue-property-decorator";
import ZonePicto from "@/components/ZonePicto.vue";
import ProductLinePanel from "@/components/orders/ProductLinePanel.vue";
import { Support, SupportStatuses } from "@/model/support/Support";
import formatDate from "@/filters/DateFormatter";
import { SupportDisplayContexts, SupportDisplayOptions } from "@/model/support/SupportDisplayOptions";
import {OrderProduct} from "@/model/OrderProduct";
import {Order} from "@/model/Order";
import {SupportProduct} from "@/model/support/SupportProduct";
import formatStoreLabel from "@/filters/StoreLabelFormatter";
import { Drive } from "@/model/Drive";
import {ModifiedQuantity} from "@/model/order/ModifiedQuantity";

@Component({
  components: { ZonePicto, ProductLinePanel },
  filters: {formatDate, formatStoreLabel}
})
export default class SupportPanel extends Vue {

  @Prop({default: null})
  protected order: Order;
  @Prop({default: null})
  protected support: Support;
  @Prop({default: () => new SupportDisplayOptions()})
  protected options: SupportDisplayOptions;
  @Prop({default: false})
  protected isActive: boolean;
  @Prop({default: false})
  protected canEditSupport: boolean;
  @Prop({default: (): Support[] => []})
  protected allSupports: Support[];
  protected isEditingSupport: boolean = false;
  protected editQuantitySnapshot: Map<string, number> = new Map();

  onHeaderClick(): void {
    if (this.isManualCollectionExpected) {
      this.$emit('headerclick', this.support);
    }
  }

  getOrderProduct(supportProduct: SupportProduct): OrderProduct {
    if (!this.order?.products) {
      return;
    }
    return this.order.products.find(product => product.cug === supportProduct.cug);
  }

  onSupportEdit(): void {
    this.isEditingSupport = true;
    this.editQuantitySnapshot = new Map(
      (this.support.products || []).map(supportProduct => [supportProduct.cug, supportProduct.processedQuantity])
    );
  }

  onSupportDelete(): void {
    this.$emit("supportdelete", { supportToDelete: this.support });
  }

  onSupportEditConfirm(): void {
    this.$emit("supportaction", { products: this.orderProducts });
    this.editQuantitySnapshot.clear();
    this.isEditingSupport = false;
  }

  onSupportEditCancel(): void {
    (this.support.products || []).forEach(supportProduct => {
      if (this.editQuantitySnapshot.has(supportProduct.cug)) {
        supportProduct.processedQuantity = this.editQuantitySnapshot.get(supportProduct.cug);
      }
    });
    this.editQuantitySnapshot.clear();
    this.isEditingSupport = false;
  }

  exitEditMode(): void {
    this.editQuantitySnapshot.clear();
    this.isEditingSupport = false;
  }

  get canConfirmSupportEdit(): boolean {
    return this.support.products != null && this.support.products.length > 0;
  }

  getOtherSupportsPreparedQuantity(cug: string): number {
    return (this.allSupports || [])
      .filter(otherSupport => otherSupport.code !== this.support?.code)
      .flatMap(otherSupport => otherSupport.products ?? [])
      .filter(supportProduct => supportProduct.cug === cug)
      .reduce((sum, supportProduct) => sum + (supportProduct.processedQuantity ?? 0), 0);
  }

  updatePreparedQuantity(modifiedQuantity: ModifiedQuantity, item: SupportProductItem): void {
    if (item.supportProduct) {
      item.supportProduct.processedQuantity = modifiedQuantity.quantity.value;
    }
  }


  get productItems(): SupportProductItem[] {
    if (!this.support?.products) {
      return [];
    }

    const orderProductsMap = new Map<string, OrderProduct>(
      this.order?.products?.map(p => [p.cug, p]) || []
    );

    return this.support.products.map(supportProduct => ({
      supportProduct,
      orderProduct: orderProductsMap.get(supportProduct.cug)
    }));
  }

  get orderProducts(): OrderProduct[] {
    return this.productItems
      .map(item => item.orderProduct)
      .filter((product): product is OrderProduct => product !== undefined);
  }

  get showOrderLabel(): boolean {
    return this.isManualCollectionExpected || this.isManuallyCollected;
  }

  get showSupportLabel(): boolean {
    return !this.isManualCollectionExpected && !this.isManuallyCollected;
  }

  get hasProducts(): boolean {
    return this.support != null
      && this.support.products != null
      && this.support.products.length > 0;
  }

  get canDisplayLocation(): boolean {
    return this.isDroppingOff;
  }

  get canDisplayDeliveryFlag(): boolean {
    return this.isCollecting || this.isDroppingOff;
  }

  get driveLabel(): string {
    const storeDrives = sessionStorage.getItem("storeDrives");
    if (storeDrives == null || storeDrives.length <= 1) {
      return this.support.driveId;
    }
    const drive: Drive = (JSON.parse(storeDrives) as Drive[])
      .find(drive => drive.driveId == this.support.driveId);
    if (drive == null) {
      return this.support.driveId;
    }
    return drive.driveLabel;
  }

  get isCollecting(): boolean {
    return this.options.context === SupportDisplayContexts.COLLECTION;
  }

  get isDroppingOff(): boolean {
    return this.options.context === SupportDisplayContexts.DROP_OFF;
  }

  get isManualCollectionExpected(): boolean {
    return this.isCollecting
      && this.support.supportStatus === SupportStatuses.TO_COLLECT
      && this.options.requiresManualAction === true;
  }

  get isManuallyCollected(): boolean {
    return this.isCollecting
      && this.support.supportStatus === SupportStatuses.COLLECTED
      && this.options.requiresManualAction === true;
  }

  get isCollectionExpected(): boolean {
    return this.isCollecting
      && this.support.supportStatus === SupportStatuses.TO_COLLECT
      && this.options.requiresManualAction === false;
  }

  get isCollectionComplete(): boolean {
    return this.isCollecting
      && this.support.supportStatus === SupportStatuses.COLLECTED;
  }

  get isDropOffExpected(): boolean {
    return this.isDroppingOff
      && this.support.supportStatus === SupportStatuses.COLLECTED;
  }

  get isDropOffComplete(): boolean {
    return this.support.supportStatus === SupportStatuses.DROPPED_OFF;
  }

  get isOutOfPlannedSlot(): boolean {
    if (!this.support?.plannedSlot || !this.support?.collectionSlot) {
      return false;
    }

    const planned = new Date(this.support.plannedSlot).getTime();
    const collected = new Date(this.support.collectionSlot).getTime();

    if (isNaN(planned) || isNaN(collected)) {
      return false;
    }

    return planned !== collected;
  }
}

interface SupportProductItem {
  supportProduct: SupportProduct;
  orderProduct: OrderProduct | undefined;
}
